import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Theme } from '@material-ui/core/styles';
import { Layout } from '@roc/feature-app-core';
import { filterProps, noOpComparator } from '@roc/ui/utils';
import { GridColumnProps, Grid, DataGrid } from '@roc/ui';
import { useInsuranceDetailsGridStore } from '../hooks/useInsuranceDetailsStore';
import { useParams } from 'react-router';
import { decodeParams, EncodedParams } from '@roc/feature-sow-v2';
import { useEffect } from 'react';
import { observer } from 'mobx-react';
const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(4),
  },
  gridContainer: {
    height: '50vh',
    marginTop: theme.spacing(2),
  },
  embeddedContainer: {
    width: '100%',
    minHeight: theme.spacing(50),
    padding: theme.spacing(2, 0),
  },
}));

const columns: GridColumnProps[] = [
  {
    field: 'propertyId',
    headerName: 'Property Id',
    minWidth: 60,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'propertyAddress',
    headerName: 'Property Address',
    minWidth: 180,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'policyNumber',
    headerName: 'Policy Number',
    minWidth: 130,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'expirationDate',
    headerName: 'Expiration Date',
    minWidth: 130,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'companyName',
    headerName: 'Company Name',
    minWidth: 130,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'agentName',
    headerName: 'Agent Name',
    minWidth: 130,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'agentPhone',
    headerName: 'Agent Phone',
    minWidth: 130,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'agentAddress',
    headerName: 'Agent Address',
    minWidth: 180,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'description',
    headerName: 'Coverage Type',
    minWidth: 180,
    comparator: noOpComparator,
    ...filterProps(),
  },
  {
    field: 'entryType',
    headerName: 'Entry Type',
    minWidth: 180,
    comparator: noOpComparator,
    ...filterProps(),
  },
];

export type InsuranceDetailsGridProps = {
  loanId?: string;
  embedded?: boolean;
};

export const InsuranceDetailsGrid = observer(({ loanId: loanIdProp, embedded = false }: InsuranceDetailsGridProps) => {
  const { loanId: loanIdParam } = useParams<{ loanId: string }>();
  const loanId = loanIdProp ?? loanIdParam;
  const classes = useStyles();

  const { insuranceDetailsGridStore } = useInsuranceDetailsGridStore();
  const { gridStore } = insuranceDetailsGridStore ?? {};

  useEffect(() => {
    if (!loanId || !gridStore) {
      return;
    }

    insuranceDetailsGridStore.setLoanId(loanId);
    gridStore.fetchGridData();
  }, [loanId, gridStore, insuranceDetailsGridStore]);

  if (!insuranceDetailsGridStore || !gridStore) {
    return null;
  }

  const onSortChanged = params => {
    const sortModel = params.api.getSortModel();
    gridStore.setSortModel(sortModel);
  };

  const onFilterChanged = params => {
    const filterModel = params.api.getFilterModel();
    gridStore.setFilterModel(filterModel);
  };

  const grid = (
    <DataGrid
      columns={columns}
      rows={gridStore.gridData.data.rows || []}
      showPagination
      onFilterChanged={onFilterChanged}
      onSortChanged={onSortChanged}
      isLoading={gridStore.isLoading}
      showFilters={gridStore.showFilters}
      sortModel={gridStore.sortModel}
      filterModel={gridStore.filterModel}
      paginationData={gridStore.gridData.meta}
      setPageNumber={gridStore.setPageNumber}
      domLayout={embedded ? 'autoHeight' : 'normal'}
    />
  );

  if (embedded) {
    return <Box className={classes.embeddedContainer}>{grid}</Box>;
  }

  return <Layout title="Insurance Details">{grid}</Layout>;
});
